import { Suspense, StrictMode } from 'react';
import ReactDOM from 'react-dom';
import * as serviceWorker from './serviceWorker';
import reportWebVitals from './reportWebVitals';
import {
  Route,
  Routes,
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { MargaretProvider } from '@koob/margaret';
import { QueryClient, QueryClientProvider } from 'react-query';
import AuthProvider from 'providers/AuthProvider';
import { KoobMaintenanceGate, KoobSnackProvider } from '@koob/ui';
import { theme } from 'ui/theme';
import { fontStyles } from 'ui';
import App from './App';
import Login from 'containers/Auth/Login';
import Signup from 'containers/Auth/Signup';
import LostAccess from 'containers/Auth/LostAccess';
import ResetPassword from 'containers/Auth/ResetPassword';
import HotelKeeper from 'containers/HotelKeeper';
import PublicTravelDiary from '@koob/trip-export/src/PublicTravelDiary';
import AuthRedirect from 'containers/Auth/AuthRedirect';
import 'sanitize.css';
import 'sanitize.css/typography.css';
import 'sanitize.css/forms.css';
import 'flag-icon-css/css/flag-icon.css';
import { initGTM } from '@koob/google-tag-manager';
import { initUmami } from '@koob/umami';
import { configureRealtime } from '@koob/realtime';
import { API_NODE_BASE } from './constants';
import './index.css';
import './i18n';

initGTM(process.env.REACT_APP_DMC_GTM_ID);
initUmami({
  scriptUrl: process.env.REACT_APP_UMAMI_SCRIPT_URL,
  websiteId: process.env.REACT_APP_DMC_UMAMI_WEBSITE_ID,
});

// EventSource cannot go through the axios instances (no custom headers), so the
// realtime client gets the base URL and the token reader directly. Impersonation
// wins, exactly like the axios request interceptor.
configureRealtime({
  baseUrl: API_NODE_BASE,
  getAccessToken: () =>
    localStorage.getItem('impersonationToken') ||
    localStorage.getItem('accessToken'),
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const GlobalStyles = createGlobalStyle`
  html {
    -webkit-font-smoothing: antialiased;
  }

  nav li::before {
    content: initial;
  }

  body {
    color: ${({ theme }) => theme.text};
    ${fontStyles.body}
  }

  .ReactModal__Overlay {
    backdrop-filter: blur(10px);
  }

  .ReactModal__Content {
    min-height: 300px;
  }
`;

// One catch-all route: the app keeps its nested <Routes>, and the data router
// gives it useBlocker (hooks/usePrompt).
const Root = () => (
  <>
    <GlobalStyles />
    <Routes>
      <Route path="account-type" element={<Navigate to="/login" replace />} />
      <Route path="login" element={<Login />} />
      <Route path="lost-access" element={<LostAccess />} />
      <Route path="password-reset" element={<ResetPassword />} />
      <Route path="validate-account" element={<Signup />} />
      <Route path="auth-redirect" element={<AuthRedirect />} />
      <Route path="hotelkeeper/:secret" element={<HotelKeeper />} />
      <Route path="travel-diary/:shareToken" element={<PublicTravelDiary />} />
      <Route path="/*" element={<App />} />
    </Routes>
  </>
);

const router = createBrowserRouter([{ path: '*', element: <Root /> }]);

ReactDOM.render(
  <StrictMode>
    <Suspense fallback={<div />}>
      <KoobMaintenanceGate apiBase={API_NODE_BASE}>
        <QueryClientProvider client={queryClient}>
          <MargaretProvider theme={theme}>
            <KoobSnackProvider>
              <AuthProvider>
                <RouterProvider router={router} />
              </AuthProvider>
            </KoobSnackProvider>
          </MargaretProvider>
        </QueryClientProvider>
      </KoobMaintenanceGate>
    </Suspense>
  </StrictMode>,
  document.getElementById('root'),
);

serviceWorker.unregister();

reportWebVitals();
